import { T, useTranslate } from '@tolgee/react';
import {
  Alert,
  Breadcrumb,
  Divider,
  Dropdown,
  Menu,
  Progress,
  Select,
  Skeleton,
  Table,
  Tabs,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import Link from 'next/link';
import {
  CSSProperties,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Hotkeys from 'react-hot-keys';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List, ListOnScrollProps } from 'react-window';
import styled, { css } from 'styled-components';

import { ArrowLeftSvg, ExclamationMarkSvg, StarFilledSvg } from '../../../assets/icons';
import AppreciateSvg from '../../../assets/icons/AppreciateSvg';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import CuripodAIStarsSvg from '../../../assets/icons/CuripodAIStarsSvg';
import Editsvg from '../../../assets/icons/EditSvg';
import FlashSvg from '../../../assets/icons/FlashSvg';
import FullScreenSvg from '../../../assets/icons/FullScreenSvg';
import MediaIconSvg from '../../../assets/icons/MediaIconSvg';
import { RecycleSvg } from '../../../assets/icons/RecycleSvg';
import ShareTemplateSvg from '../../../assets/icons/ShareTemplateSvg';
import theme from '../../../assets/theme';
import { Input } from '../../../components/_atoms/_Input';
import { Switch } from '../../../components/_atoms/_Switch';
import { Button } from '../../../components/_atoms/Button';
import ConditionalWrapper from '../../../components/_atoms/ConditionalWrapper';
import { IconWrapper } from '../../../components/_atoms/IconWrapper';
import { PremiumFeatureGateModal } from '../../../components/_molecules/BlockedModals';
import LazyDrawingResponse from '../../../components/_molecules/LazyDrawingResponse';
import ShareReportModal from '../../../components/_molecules/ShareReportModal';
import ReportParticipantMatrix from '../../../components/_organisms/ReportParticipantMatrix';
import { getParticipantPercentageColor } from '../../../components/_organisms/ReportParticipantMatrix/ReportParticipantMatrix';
import { CreateButton } from '../../../components/dynamicPages/NewLessonsPage';
import { FullScreenSlidePreview } from '../../../components/FullScreenSlidePreview';
import NewAppLayout from '../../../components/layouts/NewAppLayout';
import { parseSlateStringToText } from '../../../components/SlideComponents/slateUtil';
import {
  PollOptions,
  Slide,
  SlideType,
} from '../../../components/SlideComponents/slide.types';
import {
  findRelatedSlidesWithoutIndex,
  slideToFirstRelevanSlideIcon,
} from '../../../components/SlideComponents/slideUtil';
import { CSSSlideWrapperViewer } from '../../../components/SlideComponents/SlideWrapperViewer';
import { EmojiWrapper } from '../../../components/styled/EmojiWrapper';
import { AiAssessedScore } from '../../../frontendServices/gameAPI';
import useAccessBadge from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import { useCreatePresentationFromReport } from '../../../hooks/useCreatePresentationFromReport';
import useDeleteReportInsight from '../../../hooks/useDeleteReportInsight';
import useDuplicateTemplate from '../../../hooks/useDuplicateTemplate';
import useEditReportInsight from '../../../hooks/useEditReportInsight';
import useGame from '../../../hooks/useGame';
import useGameActivityResults from '../../../hooks/useGameActivityResults';
import useGenerateReportInsight from '../../../hooks/useGenerateReportInsight';
import useImagePreview from '../../../hooks/useImagePreview';
import useListAllAiAssessedScores from '../../../hooks/useListAllAiAssessedScores';
import useListAllAiFeedback from '../../../hooks/useListAllAIFeedbackForGame';
import { useMetrics } from '../../../hooks/useMetrics';
import { usePaywallEmailTracking } from '../../../hooks/usePaywallEmailTracking';
import { usePrevious } from '../../../hooks/usePrevious';
import useReportInsights, { ReportInsightType } from '../../../hooks/useReportInsights';
import useRoutePrefetch from '../../../hooks/useRoutePrefetch';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useScrollPosition from '../../../hooks/useScrollPosition';
import useTemplate from '../../../hooks/useTemplate';
import LiveActionsDisabledProvider from '../../../providers/LiveActionsDisabledProvider';
import NoAccess from '../../../providers/NoAccess';
import { LessonAttachment } from '../../../services/backendService/backendService';
import {
  ActivityResults,
  ActivitySlideResult,
  ById,
  Game,
  Participant,
  Response,
  Score,
} from '../../../services/backendService/types';
import { hasScorePromptOutput } from '../../../utils/aiFeedbackScore';
import { debounceEventIfNotTrusted, notUndefined } from '../../../utils/general';
import { isReportPaywalled } from '../../../utils/reportPaywall';
import { createParticipantList } from '../../../utils/reportUtils';
import { toIcon } from '../../../utils/roundUtils';
import {
  PARTICIPANT_NAME_REPLACER_PLACEHOLDER,
  replaceNameWithLink,
} from '../../../utils/stringUtils';
import { getTitle } from '../../../utils/templateUtils';
import { zIndexes } from '../../../utils/zIndexes';

const MOBILE_BREAKPOINT = 1100;

const ReportPage = () => {
  useRoutePrefetch(`/[tenantId]/lessons/[templateId]/edit`, undefined, {
    priority: false,
  });
  const { urlTenantId: tenantId, gameId, router, tab } = useRouterMatch();
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | undefined>(
    undefined,
  );
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const metrics = useMetrics();
  const trackPaywallEmail = usePaywallEmailTracking();
  const { t } = useTranslate();
  const { generateInsight, isLoading: isLoadingInsights } = useGenerateReportInsight();
  const isLargerThan830 = useBreakPoint(830);

  const handleGenerateInsight = useCallback(
    (reportType: ReportInsightType | 'all') => {
      generateInsight({ reportType });
    },
    [generateInsight],
  );
  const [selectedSlide, setSelectedSlide] = useState<Slide | undefined>(undefined);
  const [isEditingTitle, setIsEditingTitle] = useState<boolean>(false);
  const [newTitle, setNewTitle] = useState<string | undefined>(undefined);
  const { data: activityResults } = useGameActivityResults(gameId);
  const participantsList = useMemo(() => {
    if (!activityResults) return [];
    return createParticipantList(activityResults.participants);
  }, [activityResults]);

  const { game, editTitle, isFetching } = useGame(gameId);
  const { isTemplateOwner, template, isError: isTemplateError } = useTemplate(
    game?.templateId,
  );
  const { duplicateTemplate, isDuplicatingTemplate } = useDuplicateTemplate();
  const {
    createPresentationFromReport,
    isCreatingPresentationFromReport,
  } = useCreatePresentationFromReport();
  const [isCopyingLesson, setIsCopyingLesson] = useState<boolean>(false);
  const hasMissingTemplate = !!game && (!game.templateId || isTemplateError);
  const hasValidTemplate = !!game?.templateId && !!template && !isTemplateError;

  // Wait until both the access badge and the game have resolved before
  // deciding whether to gate. Otherwise userCanViewPaywalledReport(undefined)
  // briefly returns false and we'd flash the paywall at premium teachers.
  const canEvaluateGate = !!userAccessBadge && !!game;
  const isGated =
    canEvaluateGate && isReportPaywalled(userAccessBadge, game?.lastPlayedAt);
  const [isPaywallOpen, setIsPaywallOpen] = useState(false);
  const hasLoggedPaywallRef = useRef(false);

  useEffect(() => {
    if (!canEvaluateGate) return;
    if (!isGated) {
      if (hasLoggedPaywallRef.current) {
        setIsPaywallOpen(false);
        hasLoggedPaywallRef.current = false;
      }
      return;
    }
    if (hasLoggedPaywallRef.current) return;
    hasLoggedPaywallRef.current = true;
    setIsPaywallOpen(true);
    metrics.logEvent('Paywall.Prompted', {
      screen: 'reportPagePaywall',
      source: 'reportPagePaywall',
      label: 'Report page: View report',
      gameId,
      lastPlayedAt: game?.lastPlayedAt,
    });
  }, [canEvaluateGate, isGated, gameId, game?.lastPlayedAt, metrics]);

  const handlePaywallClose = useCallback(() => {
    setIsPaywallOpen(false);
    if (tenantId) {
      router.push(`/${tenantId}/reports`);
    }
  }, [router, tenantId]);

  const handlePaywallEmailSubmit = useCallback(
    (email: string) => {
      trackPaywallEmail(email, {
        paywallType: 'premium-feature-gate',
        source: 'reportPagePaywall',
        screen: 'reportPagePaywall',
      });
    },
    [trackPaywallEmail],
  );

  const handleCopyAndEdit = useCallback(() => {
    if (!game?.templateId || !tenantId || isDuplicatingTemplate) return;

    metrics.logEvent('Report.Lesson.CopyAndEdit', {
      templateId: game.templateId,
      isOwner: false,
    });

    setIsCopyingLesson(true);
    duplicateTemplate({
      templateId: game.templateId,
      isPrivate: true,
      onSuccessFunction: template => {
        setIsCopyingLesson(false);
        router.push(`/${tenantId}/lessons/${template.id}/edit`);
      },
    });
  }, [
    game?.templateId,
    tenantId,
    isDuplicatingTemplate,
    metrics,
    duplicateTemplate,
    router,
  ]);

  const handleEditFromReport = useCallback(() => {
    if (!gameId || !tenantId || isCreatingPresentationFromReport) return;

    metrics.logEvent('Report.Lesson.EditFromReport', {
      livePresentationId: gameId,
      hasTemplateId: !!game?.templateId,
      templateNotFound: isTemplateError,
    });

    createPresentationFromReport({
      livePresentationId: gameId,
      onSuccess: presentationId => {
        router.push(`/${tenantId}/lessons/${presentationId}/edit`);
      },
    });
  }, [
    gameId,
    tenantId,
    isCreatingPresentationFromReport,
    metrics,
    game?.templateId,
    isTemplateError,
    createPresentationFromReport,
    router,
  ]);

  const slidesToView = useMemo(() => {
    const slidesToCheck = game?.slides || [];
    if (slidesToCheck.length === 0) return [];

    const playedSlides = slidesToCheck
      .map(r => {
        if (r.type === 'slide') return r;
        const relatedSlides = findRelatedSlidesWithoutIndex(r.id, slidesToCheck);
        if (relatedSlides.find(r => !r.wasPlayed)) return { ...r, wasPlayed: false };
        return { ...r, wasPlayed: true };
      })
      .filter(notUndefined);
    return playedSlides;
  }, [game?.slides]);

  const relatedSlide = useMemo(() => {
    if (!selectedSlide) return;
    const relatedSlides = findRelatedSlidesWithoutIndex(selectedSlide.id, slidesToView);
    return relatedSlides[0];
  }, [selectedSlide, slidesToView]);

  const { aiAssessedScores } = useListAllAiAssessedScores({
    gameId,
    tenantId,
    slideId:
      relatedSlide &&
      (relatedSlide?.type === 'aiAssessedAnswers' ||
        relatedSlide?.type === 'aiAssessedImageAnswers')
        ? relatedSlide.id
        : undefined,
    isUsingSockets: false,
  });

  const { aiAssessedScores: allAIFeedback } = useListAllAiFeedback({
    gameId,
  });

  const reportIsApprovedForInsight = useMemo(() => {
    if (!activityResults) return false;
    if (!activityResults.slidesResults) return false;
    // checks if the results contains any reults with openended questions or Ai feedback
    return !!activityResults.slidesResults.find(r => {
      const activity = r.slides.find(
        s => s.type === 'aiAssessedAnswers' || s.type === 'answers',
      );
      if (!activity) return false;
      return activity.wasPlayed;
    });
  }, [activityResults]);
  const selectedSlidesResultAllParticipants:
    | ActivitySlideResult
    | undefined = useMemo(() => {
    if (!selectedSlide) return;
    if (selectedSlide.type === 'slide') return;
    if (!activityResults) return;

    const slidesResult = activityResults.slidesResults.find(r => {
      return r.slides.find(s => s.id === selectedSlide?.id);
    });

    //Find participants that did not participate in the activity
    const participantsThatDidNotParticipate = createParticipantList(
      activityResults.participants,
    )?.filter(p => {
      const score = slidesResult?.scores.find(s => s.response.uid === p.id);
      if (!score) return true;
      return false;
    });

    const nonRespondantResponses: Score[] = participantsThatDidNotParticipate?.map(p => {
      return {
        id: 'nonResponse',
        responseId: 'nonResponse',
        response: {
          id: 'nonResponse',
          uid: p.id,
          response: 'No response submitted',
          question: '',
        },
        score: 0,
        question: {
          id: 'nonResponse',
          uid: p.id,
          response: 'No response submitted',
          question: '',
        },
        questionId: '',
      };
    });

    if (
      !slidesResult ||
      !slidesResult.wasPlayed ||
      !slidesResult.scores ||
      slidesResult.scores.length === 0
    ) {
      return;
    }

    //Add the nonResponseResponse to the scores list
    const newScoresWithNonResponse = [...slidesResult.scores, ...nonRespondantResponses];
    // Appends the Ai score to the score
    if (aiAssessedScores) {
      const scoresWithAiFeedback: Score[] = newScoresWithNonResponse.map(r => {
        const feedback = aiAssessedScores.find(
          aiScore => aiScore.responseId === r.response.id,
        );
        return { ...r, feedback };
      });
      return {
        ...slidesResult,
        scores: scoresWithAiFeedback,
      };
    }

    return { ...slidesResult, scores: newScoresWithNonResponse };
  }, [activityResults, aiAssessedScores, selectedSlide]);

  const title = useMemo(() => {
    if (!game) return '';
    return getTitle({
      title: game.title,
      slides: game.slides,
    });
  }, [game]);

  const slidesToRender = useMemo(() => {
    if (!slidesToView) return [];
    return slidesToView.filter(s => {
      if (s.type === 'slide') return true;
      if (s.type === 'poll') return true;
      if (s.type === 'multipleChoice') return true;
      if (s.type === 'wordcloud') return true;
      if (
        s.type === 'answers' ||
        s.type === 'canvas' ||
        s.type === 'aiAssessedAnswers' ||
        s.type === 'aiAssessedImageAnswers'
      ) {
        const relatedSlides = findRelatedSlidesWithoutIndex(s.id, slidesToView);
        // we found a slide that was not played so we should show the podium/responsegrid
        if (relatedSlides.find(r => r.wasPlayed)) return false;
        return true;
      }
      if (s.type === 'podium' || s.type === 'responsegrid') {
        const relatedSlides = findRelatedSlidesWithoutIndex(s.id, slidesToView);
        if (relatedSlides.find(r => !r.wasPlayed)) return false;
        return true;
      }

      return false;
    });
  }, [slidesToView]);
  const shareMenu = (
    <Menu style={{ maxWidth: 340 }}>
      <ShareReportModal gameId={gameId} />
    </Menu>
  );

  const setSelectedTab = useCallback(
    (tab: string) => {
      router.push(`/${tenantId}/reports/${gameId}?tab=${tab}`);
    },
    [gameId, router, tenantId],
  );

  useEffect(() => {
    if (!slidesToRender) return;
    if (!!selectedSlide) return;
    setSelectedSlide(slidesToRender[0]);
  }, [selectedSlide, slidesToRender]);

  return (
    <NewAppLayout
      breakPoint={MOBILE_BREAKPOINT}
      onBackClick={() => router.push(`/${tenantId}/lessons`)}
    >
      <PremiumFeatureGateModal
        visible={isPaywallOpen}
        onClose={handlePaywallClose}
        country={userAccessBadge?.organization?.country}
        source="reportPagePaywall"
        screen="reportPagePaywall"
        onEmailSubmit={handlePaywallEmailSubmit}
      />
      <ResultPageContentContainer>
        <NoAccess
          description={t(
            'noAccessToReport',
            'We’d also love to see how all your amazing students did this lesson, but because you are not the teacher that taught this Curipod, you do not have access to view the report data.\n\nPlease try another report.',
          )}
          condition={
            !!game &&
            !!userAccessBadge?.user &&
            userAccessBadge?.user.id === game.host.uid
          }
          isLoading={isFetching && !game}
        >
          <PaywallBlurWrapper $isGated={isGated}>
            {game && (
              <Container>
                <ReportContainer>
                  <ReportContainerHeader>
                    <ReportContainerHeaderContent>
                      <Breadcrumb
                        style={{
                          fontWeight: 'bold',
                          padding: '20px 0 0 20px',
                          width: '90%',
                        }}
                        separator="•"
                      >
                        <Breadcrumb.Item>
                          <Link href={`/${tenantId}/reports`}>
                            <span
                              style={{
                                cursor: 'pointer',
                                padding: 0,
                                color: theme.colors.black,
                              }}
                            >
                              {'Report'}
                            </span>
                          </Link>
                        </Breadcrumb.Item>
                        {hasValidTemplate && (
                          <Breadcrumb.Item>
                            {isTemplateOwner ? (
                              <Link
                                href={`/${tenantId}/lessons/${game?.templateId}/edit`}
                              >
                                <span
                                  style={{
                                    cursor: 'pointer',
                                    padding: 0,
                                    color: theme.colors.black,
                                    textDecoration: 'underline',
                                  }}
                                >
                                  {t('editLesson', 'Edit lesson')}
                                </span>
                              </Link>
                            ) : (
                              <span
                                style={{
                                  cursor: 'pointer',
                                  padding: 0,
                                  color: theme.colors.black,
                                  textDecoration: 'underline',
                                }}
                                onClick={handleCopyAndEdit}
                              >
                                {isCopyingLesson || isDuplicatingTemplate
                                  ? t('copyingLesson', 'Copying lesson...')
                                  : t('copyAndEditLesson', 'Copy and edit lesson')}
                              </span>
                            )}
                          </Breadcrumb.Item>
                        )}
                        {hasMissingTemplate && (
                          <Breadcrumb.Item>
                            <span
                              style={{
                                cursor: 'pointer',
                                padding: 0,
                                color: theme.colors.black,
                                textDecoration: 'underline',
                              }}
                              onClick={handleEditFromReport}
                            >
                              {isCreatingPresentationFromReport
                                ? t('creatingLesson', 'Creating lesson...')
                                : t('editLesson', 'Edit lesson')}
                            </span>
                          </Breadcrumb.Item>
                        )}
                        {!isLargerThan830 && (
                          <Breadcrumb.Item>
                            <Dropdown
                              placement="bottom"
                              trigger={['click']}
                              overlay={shareMenu}
                            >
                              <span
                                style={{
                                  cursor: 'pointer',
                                  padding: 0,
                                  color: theme.colors.black,
                                }}
                              >
                                {t('share', 'Share')}
                              </span>
                            </Dropdown>
                          </Breadcrumb.Item>
                        )}
                      </Breadcrumb>
                      {!isEditingTitle && (
                        <ResultTitle>
                          {title.length > 40 ? `${title.slice(0, 40)}...` : title}
                          <IconWrapper onClick={() => setIsEditingTitle(true)}>
                            <Editsvg />
                          </IconWrapper>
                        </ResultTitle>
                      )}
                      {isEditingTitle && (
                        <div
                          style={{
                            display: 'flex',
                            gap: '10px',
                            alignItems: 'center',
                            padding: '0 20px',
                          }}
                        >
                          <EditTitleInput
                            style={{ width: '80%' }}
                            value={newTitle || title}
                            autoFocus={true}
                            onChange={e => setNewTitle(e.target.value)}
                            onPressEnter={() => {
                              if (newTitle && newTitle !== title) {
                                metrics.logEvent('Report.Title.Edit', {
                                  participantLength: participantsList.length,
                                  onEnter: true,
                                });
                                editTitle({
                                  title: newTitle || title,
                                  gameId,
                                });
                              }
                              setIsEditingTitle(false);
                            }}
                            onBlur={() => {
                              if (newTitle && newTitle !== title) {
                                metrics.logEvent('Report.Title.Edit', {
                                  participantLength: participantsList.length,
                                  onBlur: true,
                                });
                                editTitle({
                                  title: newTitle || title,
                                  gameId,
                                });
                              }
                              setIsEditingTitle(false);
                            }}
                          />
                        </div>
                      )}

                      <ResultsTab
                        defaultActiveKey="summary"
                        activeKey={tab}
                        onChange={v => setSelectedTab(v)}
                        size="large"
                      >
                        <Tabs.TabPane
                          tab="Summary"
                          key="summary"
                          style={{ paddingLeft: '20px' }}
                        ></Tabs.TabPane>
                        <Tabs.TabPane
                          tab="Students"
                          key="students"
                          disabled={!participantsList || participantsList.length === 0}
                        ></Tabs.TabPane>
                        <Tabs.TabPane
                          tab="Activities"
                          key="activity"
                          disabled={!game.slides || game.slides.length === 0}
                        ></Tabs.TabPane>
                      </ResultsTab>
                    </ReportContainerHeaderContent>
                    {isLargerThan830 && (
                      <ShareContainer>
                        <Dropdown
                          placement="bottomRight"
                          trigger={['click']}
                          overlay={shareMenu}
                        >
                          <ShareButton type="ghost" icon={<ShareTemplateSvg />}>
                            {t('share', 'Share')}
                          </ShareButton>
                        </Dropdown>

                        <Divider style={{ margin: '0' }} />
                        {game.lastPlayedAt && (
                          <span>
                            {dayjs(game.lastPlayedAt).format('MMMM D - YYYY - HH:mm')}{' '}
                          </span>
                        )}
                      </ShareContainer>
                    )}
                  </ReportContainerHeader>

                  <ReportContainerContent>
                    {(!tab || tab === 'summary') && (
                      <SummaryTabPane
                        activityResults={activityResults}
                        participantsList={participantsList}
                        setSelectedParticipant={setSelectedParticipant}
                        setSelectedTab={setSelectedTab}
                        livePresentationId={game.id}
                        setSelectedSlide={setSelectedSlide}
                        isLoadingInsights={isLoadingInsights}
                        handleGenerateInsight={handleGenerateInsight}
                        reportIsApprovedForInsight={reportIsApprovedForInsight}
                      />
                    )}
                    {tab === 'students' && (
                      <StudentsTabPane
                        game={game}
                        slides={slidesToView}
                        tenantId={tenantId}
                        participantsList={participantsList}
                        setSelectedParticipant={setSelectedParticipant}
                        selectedParticipant={selectedParticipant || participantsList?.[0]}
                        setSelectedSlide={setSelectedSlide}
                        selectedSlide={selectedSlide}
                        slidesToRender={slidesToRender}
                        activityResults={activityResults}
                        setSelectedTab={setSelectedTab}
                        allAIFeedback={allAIFeedback}
                        reportIsApprovedForInsight={reportIsApprovedForInsight}
                      />
                    )}
                    {tab === 'activity' && activityResults && (
                      <ActivityTabPane
                        game={game}
                        activityResults={activityResults}
                        setSelectedTab={setSelectedTab}
                        tenantId={tenantId}
                        slides={slidesToView}
                        slidesToRender={slidesToRender}
                        gameId={gameId}
                        selectedSlidesResultAllParticipants={
                          selectedSlidesResultAllParticipants
                        }
                        participantsList={participantsList}
                        setSelectedParticipant={setSelectedParticipant}
                        selectedParticipant={selectedParticipant}
                        setSelectedSlide={setSelectedSlide}
                        activityType={relatedSlide?.type || 'slide'}
                        selectedSlide={selectedSlide || slidesToView?.[0]}
                      />
                    )}
                  </ReportContainerContent>
                </ReportContainer>
              </Container>
            )}
          </PaywallBlurWrapper>
        </NoAccess>
      </ResultPageContentContainer>
    </NewAppLayout>
  );
};

const PaywallBlurWrapper = styled.div<{ $isGated: boolean }>`
  ${({ $isGated }) =>
    $isGated &&
    css`
      filter: blur(8px);
      pointer-events: none;
      user-select: none;
    `}
`;

const EditTitleInput = styled(Input)`
  height: 40px;
  input {
    font-weight: bold;
    font-size: 20px;
    background-color: ${theme.colors.greyLight};
  }
`;
const SummaryTabPane = ({
  activityResults,
  participantsList,
  livePresentationId,
  setSelectedParticipant,
  setSelectedTab,
  setSelectedSlide,
  handleGenerateInsight,
  isLoadingInsights,
  reportIsApprovedForInsight,
}: {
  activityResults?: ActivityResults;
  participantsList: Participant[];
  livePresentationId: string;
  setSelectedParticipant: Dispatch<Participant | undefined>;
  setSelectedTab: (tab: string) => void;
  setSelectedSlide: Dispatch<SetStateAction<Slide | undefined>>;
  handleGenerateInsight: (reportType: ReportInsightType | 'all') => void;
  isLoadingInsights?: boolean;
  reportIsApprovedForInsight?: boolean;
}) => {
  const metrics = useMetrics();
  const isLargerThan830 = useBreakPoint(830);
  const { data: lessonSummaryInsight, error } = useReportInsights({
    reportType: 'lessonSummary',
    livePresentationId,
    reportIsApprovedForInsight,
  });

  return (
    <SummaryContainer>
      {reportIsApprovedForInsight && (
        <SummaryLeftColumn>
          {!lessonSummaryInsight && (
            <p>
              Welcome to the report page. Click the button below to generate insights from
              your lesson.{' '}
            </p>
          )}
          {!lessonSummaryInsight && (
            <CreateButton
              style={{ maxWidth: 'fit-content' }}
              icon={!!error ? <ExclamationMarkSvg /> : <CuripodAIStarsSvg />}
              onClick={() => {
                metrics.logEvent('Report.Insight.All.Generate', {
                  participantLength: participantsList.length,
                  slidesResultsLength: activityResults?.slidesResults?.length,
                });
                handleGenerateInsight('all');
              }}
              loading={isLoadingInsights}
              disabled={!!error}
            >
              <T keyName="Generate insights" defaultValue="Generate insights" />
              <span
                style={{
                  fontSize: 12,
                  color: theme.colors.orange,
                  background: 'rgba(255,255,255,0.8)',
                  borderRadius: 7,
                  padding: '0 8px',
                  marginLeft: 10,
                }}
              >
                <T keyName="ai" defaultValue="AI" />
              </span>
            </CreateButton>
          )}
          {!!error && (
            <Alert
              message="We're sorry! 😔 There was an error generating feedback"
              type="error"
            />
          )}
          <ReportInsight
            reportType="learningObjective"
            label="Learning objective"
            livePresentationId={livePresentationId}
            participantsList={participantsList}
            regeneratable={false}
            setSelectedParticipant={setSelectedParticipant}
            setSelectedTab={setSelectedTab}
            editable={false}
          />
          {lessonSummaryInsight && (
            <ReportInsight
              reportType="lessonSummary"
              label="Lesson summary"
              livePresentationId={livePresentationId}
              regeneratable={true}
              participantsList={participantsList}
              setSelectedParticipant={setSelectedParticipant}
              setSelectedTab={setSelectedTab}
            />
          )}

          {!isLargerThan830 && (
            <ParticipantsMatrixContainer>
              <InsightText>Participation {`(${participantsList.length})`}</InsightText>
              <ReportParticipantMatrix
                activityResults={activityResults}
                participantsList={participantsList}
                setSelectedParticipant={setSelectedParticipant}
                setSelectedTab={setSelectedTab}
                reportTab={'summary'}
                setSelectedSlide={setSelectedSlide}
              />
            </ParticipantsMatrixContainer>
          )}
          <ReportInsight
            reportType="whoNeedsHelp"
            label="Who needs help"
            livePresentationId={livePresentationId}
            participantsList={participantsList}
            setSelectedParticipant={setSelectedParticipant}
            setSelectedTab={setSelectedTab}
          />
          <ReportInsight
            reportType="whoDidGreat"
            label="Who did great"
            livePresentationId={livePresentationId}
            participantsList={participantsList}
            setSelectedParticipant={setSelectedParticipant}
            setSelectedTab={setSelectedTab}
          />
        </SummaryLeftColumn>
      )}
      {isLargerThan830 && (
        <SummaryRightColumn>
          <ParticipantsMatrixContainer>
            <InsightText>Participation {`(${participantsList.length})`}</InsightText>
            <ReportParticipantMatrix
              activityResults={activityResults}
              participantsList={participantsList}
              reportTab={'summary'}
              setSelectedParticipant={setSelectedParticipant}
              setSelectedTab={setSelectedTab}
              setSelectedSlide={setSelectedSlide}
            />
          </ParticipantsMatrixContainer>
        </SummaryRightColumn>
      )}
    </SummaryContainer>
  );
};

const ReportInsight = ({
  reportType,
  editable,
  label,
  participantsList,
  livePresentationId,
  participantId,
  setSelectedParticipant,
  setSelectedTab,
  regeneratable,
}: {
  editable?: boolean;
  label: string;
  reportType: ReportInsightType;
  participantsList: Participant[];
  livePresentationId: string;
  participantId?: string;
  setSelectedParticipant?: Dispatch<Participant | undefined>;
  setSelectedTab: (tab: string) => void;
  regeneratable?: boolean;
}) => {
  const metrics = useMetrics();
  const [isEditingInsight, setIsEditingInsight] = useState<boolean>(false);
  const [newInsight, setNewInsight] = useState<string | undefined>(undefined);
  const { generateInsight, isLoading: isLoadingInsights } = useGenerateReportInsight();
  const { editReportInsight } = useEditReportInsight();
  const { deleteReportInsight } = useDeleteReportInsight();
  const { data: insightData } = useReportInsights({
    reportType,
    livePresentationId,
    participantId,
    reportIsApprovedForInsight: true,
  });

  const deleteLearningObjectiveDependencies = useCallback(() => {
    deleteReportInsight({
      reportType: 'lessonSummary',
    });
    deleteReportInsight({
      reportType: 'whoNeedsHelp',
    });
    deleteReportInsight({
      reportType: 'whoDidGreat',
    });
  }, [deleteReportInsight]);

  const handleSaveInsight = useCallback(() => {
    if (!insightData || !newInsight) return;
    if (!editable) return;
    editReportInsight({
      insightId: insightData.id,
      promptOutput: newInsight || '',
      reportType,
    });
    if (reportType === 'learningObjective') {
      deleteLearningObjectiveDependencies();
    }
  }, [
    deleteLearningObjectiveDependencies,
    editReportInsight,
    editable,
    insightData,
    newInsight,
    reportType,
  ]);

  if (!insightData) return null;
  return (
    <SummaryContainerItem>
      <InsightText style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 16 }}>{label}</span>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            transform: 'translate(0, -50%)',
          }}
        >
          {regeneratable && (
            <RecyrcleIconWrapper
              $isLoading={isLoadingInsights}
              onClick={() => {
                metrics.logEvent('Report.Insight.Regenerate', {
                  reportType,
                  participantLength: participantsList.length,
                });
                if (!isLoadingInsights && regeneratable) {
                  generateInsight({ reportType, participantId });
                  if (reportType === 'learningObjective') {
                    deleteLearningObjectiveDependencies();
                  }
                }
              }}
            >
              <RecycleSvg />
            </RecyrcleIconWrapper>
          )}
          {editable && (
            <IconWrapper onClick={() => setIsEditingInsight(!isEditingInsight)}>
              <Editsvg />
            </IconWrapper>
          )}
        </div>
      </InsightText>
      {!isEditingInsight && (
        <EnrichedPromptsInsights
          isLoading={isLoadingInsights}
          participantsList={participantsList}
          insightText={insightData.promptOutput}
          setSelectedParticipant={setSelectedParticipant}
          setSelectedTab={setSelectedTab}
        />
      )}
      {isEditingInsight && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <TextArea
            value={newInsight || insightData.promptOutput}
            onChange={e => setNewInsight(e.target.value)}
          />
          <Button
            onClick={() => {
              metrics.logEvent('Report.Insight.Edit', {
                reportType,
                participantLength: participantsList.length,
              });
              setIsEditingInsight(false);
              handleSaveInsight();
            }}
            type="ghost"
          >
            Save
          </Button>
        </div>
      )}
    </SummaryContainerItem>
  );
};

const RecyrcleIconWrapper = styled(IconWrapper)<{ $isLoading?: boolean }>`
  opacity: ${({ $isLoading }) =>
    $isLoading &&
    css`
      -webkit-animation: spin 2s linear infinite;
      animation: spin 2s linear infinite;
      @keyframes spin {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    `};
`;

const SummaryLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  @media (max-width: 830px) {
    align-items: center;
  }
`;

const SummaryRightColumn = styled.div``;

const SummaryContainer = styled.div`
  display: grid;
  grid-template-columns: 400px minmax(0, 1fr);
  flex-wrap: wrap;
  gap: 10px;
  padding: 0px 20px;
  width: 100%;
  @media (max-width: 830px) {
    grid-template-columns: 1fr;
    justify-content: center;
  }
`;

const SummaryContainerItem = styled.div`
  background-color: ${theme.colors.white};
  border-radius: 10px;
  display: flex;
  font-weight: bold;
  height: min-content;
  flex-direction: column;
  position: relative;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
  padding: 20px;
  width: 100%;
  min-width: 100px;
  max-width: 500px;
  overflow: auto;
  white-space: pre-wrap;
  gap: 10px;
`;
const ParticipantsMatrixContainer = styled(SummaryContainerItem)`
  padding: 20px 0 20px 20px;
  width: min-content;
  min-width: 250px;
  max-width: 100%;
  @media (max-width: 830px) {
    max-width: 500px;
    width: 100%;
  }
`;
const InsightText = styled.span`
  white-space: pre-wrap;
  width: 100%;
`;

const EnrichedPromptsInsights = ({
  insightText,
  participantsList,
  setSelectedParticipant,
  setSelectedTab,
  isLoading,
}: {
  participantsList: Participant[];
  insightText: string;
  setSelectedParticipant?: Dispatch<Participant | undefined>;
  setSelectedTab: (tab: string) => void;
  isLoading?: boolean;
}) => {
  const participantsByNickname: ById<Participant> = useMemo(() => {
    if (!participantsList) return {};
    return participantsList.reduce((acc, participant) => {
      return { ...acc, [participant.nickName]: participant };
    }, {});
  }, [participantsList]);
  const enrichedData: ReactNode[] = useMemo(() => {
    // looking through the string of the data and if we find a nickname we replace the name with a span html element

    if (!insightText) return [];
    const data = replaceNameWithLink(
      Object.keys(participantsByNickname),
      insightText,
    ).split(PARTICIPANT_NAME_REPLACER_PLACEHOLDER);
    return data.map((word, index) => {
      const participant = participantsByNickname[word];
      if (!participant)
        return (
          <span
            key={index}
            style={{
              whiteSpace: 'pre-wrap',
            }}
          >
            {word}
          </span>
        );
      const nameTag = participant.name
        ? `${participant.name} (${participant.nickName})`
        : participant.nickName;

      return (
        <span
          key={index}
          style={{
            fontWeight: 'bold',
            color: theme.colors.green,
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onClick={() => {
            if (!setSelectedParticipant) return;
            setSelectedParticipant(participant);
            setSelectedTab('students');
          }}
        >
          {nameTag}
        </span>
      );
    });
  }, [participantsByNickname, setSelectedParticipant, setSelectedTab, insightText]);
  if (isLoading) return <Skeleton active />;
  return <span>{enrichedData}</span>;
};
const StudentsTabPane = ({
  game,
  slides,
  slidesToRender,
  participantsList,
  setSelectedParticipant,
  selectedParticipant,
  setSelectedSlide,
  selectedSlide,
  tenantId,
  activityResults,
  setSelectedTab,
  allAIFeedback,
  reportIsApprovedForInsight,
}: {
  game: Game;
  slides: Slide[];
  slidesToRender: Slide[];
  participantsList?: Participant[];
  setSelectedParticipant: Dispatch<Participant | undefined>;
  selectedParticipant?: Participant;
  selectedSlide?: Slide;
  tenantId?: string;
  setSelectedSlide: Dispatch<SetStateAction<Slide | undefined>>;
  allAIFeedback?: { slideId: string; aiAssessedScores: AiAssessedScore[] }[] | undefined;
  activityResults?: ActivityResults;
  reportIsApprovedForInsight?: boolean;

  setSelectedTab: (tab: string) => void;
}) => {
  const { t } = useTranslate();
  const [showSlideModal, setShowSlideModal] = useState<boolean>(false);
  const [showResultsView, setShowResultsView] = useState<boolean>(true);
  const isLargerThan800 = useBreakPoint(800);
  const { imagePreviewComponent, onImageClicked } = useImagePreview();
  const { scrollPosition } = useScrollPosition();
  const relatedSelectedSlides = useMemo(() => {
    if (!selectedSlide) return;
    return findRelatedSlidesWithoutIndex(selectedSlide.id, slides);
  }, [selectedSlide, slides]);
  const onlyFirstRelatedSlides = useMemo(() => {
    return slides.filter(
      s => s.type !== 'voting' && s.type !== 'responsegrid' && s.type !== 'podium',
    );
  }, [slides]);
  const studentAnswersResults = useMemo(() => {
    if (!selectedParticipant) return;
    return slides
      .map(slide => {
        if (slide.type === 'slide') return;
        if (slide.type === 'responsegrid') return;
        if (slide.type === 'podium') return;
        if (slide.type === 'voting') return;
        if (!activityResults) return;
        if (!slide.wasPlayed) return; // hide from this list if the activity was not played

        const relatedSlides = findRelatedSlidesWithoutIndex(slide.id, slides);
        const votingEnabled =
          !!relatedSlides?.[1] && relatedSlides?.[1].type === 'voting';
        const slideTitleElement = slide.elements.find(
          e => e.type === 'text' && e.lockedFromDeletion && e.lockedPosition,
        );
        const slideTitle = parseSlateStringToText(slideTitleElement?.textValue);

        const isAiFeedback =
          slide.type === 'aiAssessedAnswers' || slide.type === 'aiAssessedImageAnswers';

        const aiAssessedScoreForSlide = isAiFeedback
          ? allAIFeedback
              ?.find(result => result.slideId === slide.id)
              ?.aiAssessedScores.find(score => score.uid === selectedParticipant.id)
          : undefined;
        const feedback = aiAssessedScoreForSlide?.feedback;
        const aiScore = aiAssessedScoreForSlide?.aiScore;
        const showScore = isAiFeedback && hasScorePromptOutput(slide);

        const slidesResult = activityResults.slidesResults.find(r => {
          return r.slides.find(s => s.id === slide.id);
        });
        if (!slidesResult || !slidesResult.wasPlayed || !slidesResult.scores)
          return {
            slide: slide,
            title: slideTitle,
            responses: [],
          };
        const studentResponses = slidesResult.scores.reduce((acc, score) => {
          if (score.response.uid !== selectedParticipant.id) return acc;
          if (slide.type === 'poll' || slide.type === 'multipleChoice') {
            const pollOptions = slide.pollOptions;
            if (!pollOptions) return acc;
            const option = pollOptions.find(o => o.id === score.response.response);
            if (!option) return acc;
            const parsedPollOption = parseSlateStringToText(option.displayName);
            // For multipleChoice we also surface correctness so the student
            // tab can show a Correct/Incorrect badge alongside the answer.
            const isCorrect =
              slide.type === 'multipleChoice' ? !!option.isCorrect : undefined;
            return [
              ...acc,
              {
                ...score,
                response: { ...score.response, response: parsedPollOption },
                isCorrect,
              },
            ];
          }
          return [...acc, score];
        }, [] as Array<Score & { isCorrect?: boolean }>);
        return {
          slide,
          feedback,
          aiScore,
          showScore,
          responses: studentResponses,
          votingEnabled,
          title: slideTitle,
        };
      })
      .filter(notUndefined);
  }, [activityResults, allAIFeedback, selectedParticipant, slides]);

  const studentSelectOptions = useMemo(() => {
    if (!participantsList) return [];
    return participantsList.map(p => ({
      value: p.id,
      label: p.name ? `${p.name} (${p.nickName})` : p.nickName,
    }));
  }, [participantsList]);

  const handleUpArrowClick = useCallback(() => {
    if (!participantsList || participantsList.length === 0) return;
    if (!selectedParticipant) return;
    const indexOfSelectedParticipant = participantsList.findIndex(
      p => p.id === selectedParticipant.id,
    );

    if (indexOfSelectedParticipant === -1) {
      setSelectedParticipant(participantsList[0]);
      return;
    }
    // If the selected participant is the last one, select the first one
    if (indexOfSelectedParticipant === 0) {
      setSelectedParticipant(participantsList[participantsList.length - 1]);
      return;
    }
    setSelectedParticipant(participantsList[indexOfSelectedParticipant - 1]);
    return;
  }, [participantsList, selectedParticipant, setSelectedParticipant]);

  const handleDownArrowClick = useCallback(() => {
    if (!participantsList || participantsList.length === 0) return;
    if (!selectedParticipant) return;
    const indexOfSelectedParticipant = participantsList.findIndex(
      p => p.id === selectedParticipant.id,
    );

    if (indexOfSelectedParticipant === -1) {
      setSelectedParticipant(participantsList[0]);
      return;
    }
    // If the selected participant is the first one, select the last one
    if (indexOfSelectedParticipant === participantsList.length - 1) {
      setSelectedParticipant(participantsList[0]);
      return;
    }
    setSelectedParticipant(participantsList[indexOfSelectedParticipant + 1]);
  }, [participantsList, selectedParticipant, setSelectedParticipant]);

  return (
    <StudentsTabPaneContainer>
      {showSlideModal && relatedSelectedSlides && selectedSlide && (
        <FullScreenSlidePreview onClose={() => setShowSlideModal(false)}>
          <LiveActionsDisabledProvider>
            <CSSSlideWrapperViewer
              aspect={{ height: 1080, width: 1920 }}
              slide={relatedSelectedSlides[relatedSelectedSlides.length - 1]}
              backgroundMedia={relatedSelectedSlides[0].backgroundMedia}
              slides={slides}
              resultView={showResultsView}
              showStaticPoll={!showResultsView}
              gameId={game.id}
              tenantId={tenantId}
              hideLogo={false}
              isDiscoverTemplate={false}
              isOwner={true}
              isAuthenticated={true}
              isUsingSockets={false}
              lessonAttachments={game.attachments}
              uniqueId={`slide-result-view-${selectedSlide.id}`}
            >
              {selectedSlide.type !== 'slide' && selectedSlide.wasPlayed && (
                <SlideActionButtonContainer>
                  {showResultsView && (
                    <SlideActionButton
                      onClick={() => {
                        setShowResultsView(false);
                      }}
                    >
                      <IconWrapper width="50px" height="50px">
                        <MediaIconSvg />
                      </IconWrapper>
                      {t('showSlide', 'Show Slide')}
                    </SlideActionButton>
                  )}
                  {!showResultsView && (
                    <SlideActionButton
                      onClick={() => {
                        setShowResultsView(true);
                      }}
                    >
                      <IconWrapper width="50px" height="50px">
                        <FlashSvg />
                      </IconWrapper>
                      {t('showResults', 'Show results')}
                    </SlideActionButton>
                  )}
                </SlideActionButtonContainer>
              )}
            </CSSSlideWrapperViewer>
          </LiveActionsDisabledProvider>
        </FullScreenSlidePreview>
      )}
      <Hotkeys
        keyName={'up'}
        onKeyDown={(_, event) => {
          event.preventDefault();
        }}
        onKeyUp={() => {
          handleUpArrowClick();
        }}
      />
      <Hotkeys
        keyName={'down'}
        onKeyDown={(_, event) => {
          event.preventDefault();
        }}
        onKeyUp={() => {
          handleDownArrowClick();
        }}
      />
      <Hotkeys
        keyName={'right'}
        onKeyDown={(_, event) => {
          event.preventDefault();
        }}
        onKeyUp={() => {
          if (!slidesToRender) return;
          if (!selectedSlide) return;

          const indexOfSelectedSlide = slidesToRender.findIndex(
            s => s.id === selectedSlide?.id,
          );
          if (indexOfSelectedSlide === -1) {
            setSelectedSlide(slidesToRender[0]);
            return;
          }
          // If the selected slide is the last one, select the first one
          if (indexOfSelectedSlide === slidesToRender.length - 1) {
            setSelectedSlide(slidesToRender[0]);
            return;
          }
          setSelectedSlide(slidesToRender[indexOfSelectedSlide + 1]);
        }}
      />
      <Hotkeys
        keyName={'left'}
        onKeyDown={(_, event) => {
          event.preventDefault();
        }}
        onKeyUp={() => {
          if (!slidesToRender) return;
          if (!selectedSlide) return;
          const indexOfSelectedSlide = slidesToRender.findIndex(
            s => s.id === selectedSlide?.id,
          );
          if (indexOfSelectedSlide === -1) {
            setSelectedSlide(slidesToRender[0]);
            return;
          }
          // If the selected slide is the first one, select the last one
          if (indexOfSelectedSlide === 0) {
            setSelectedSlide(slidesToRender[slidesToRender.length - 1]);
            return;
          }
          setSelectedSlide(slidesToRender[indexOfSelectedSlide - 1]);
        }}
      />
      {imagePreviewComponent}

      <StudentDataContainer>
        <StudentsContainer>
          <ConditionalWrapper
            condition={scrollPosition > 180 && isLargerThan800}
            truthyWrapper={(children: ReactNode) => {
              return (
                <FixedStudentSelectorPositionContainer
                  style={{ top: '50px', width: '256px' }}
                >
                  {children}
                </FixedStudentSelectorPositionContainer>
              );
            }}
          >
            <>
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <p style={{ marginBottom: 0, fontSize: 16, paddingLeft: 0 }}>Students</p>
                <div style={{ display: 'flex' }}>
                  <Tooltip mouseEnterDelay={1} title="Press ⬆ to select the next student">
                    <IconWrapper
                      style={{ rotate: '90deg' }}
                      width="25px"
                      height="25px"
                      onClick={() => {
                        handleUpArrowClick();
                      }}
                    >
                      <ArrowLeftSvg />
                    </IconWrapper>
                  </Tooltip>
                  <Tooltip mouseEnterDelay={1} title="Press ⬇ to select the next student">
                    <IconWrapper
                      style={{ rotate: '90deg' }}
                      width="25px"
                      height="25px"
                      onClick={() => {
                        handleDownArrowClick();
                      }}
                    >
                      <ArrowRightSvg />
                    </IconWrapper>
                  </Tooltip>
                </div>
              </div>
              <StudentSelectContainer>
                <Select
                  showSearch
                  style={{
                    width: '100%',

                    borderRadius: '10px',
                  }}
                  placeholder="Select student"
                  notFoundContent="No students found"
                  value={selectedParticipant?.id}
                  suffixIcon={
                    <IconWrapper
                      style={{ transform: 'rotate(270deg)' }}
                      width={'20px'}
                      height={'20px'}
                    >
                      <ArrowLeftSvg />
                    </IconWrapper>
                  }
                  onChange={value => {
                    const participant = participantsList?.find(
                      p => p.id === (value as string),
                    );
                    setSelectedParticipant(participant);
                  }}
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  options={studentSelectOptions}
                />
              </StudentSelectContainer>
            </>
          </ConditionalWrapper>
        </StudentsContainer>
        {!selectedParticipant && (
          <EmptyStateContainer>
            <EmojiWrapper>
              <AppreciateSvg />
            </EmojiWrapper>
            <p style={{ color: theme.colors.black }}>
              Select a student to see their responses
            </p>
          </EmptyStateContainer>
        )}
        {selectedParticipant && (
          <StudentInformationContainer>
            <StudentSummaryView
              participant={selectedParticipant}
              livePresentationId={game.id}
              activityResults={activityResults}
              setSelectedSlide={setSelectedSlide}
              setSelectedTab={setSelectedTab}
              numberOfParticipants={participantsList?.length}
              reportIsApprovedForInsight={reportIsApprovedForInsight}
            />

            {studentAnswersResults &&
              studentAnswersResults.map((result, index) => {
                const isDrawing =
                  result.slide.type === 'canvas' ||
                  result.slide.type === 'aiAssessedImageAnswers';
                const isAIFeedback =
                  result.slide.type === 'aiAssessedAnswers' ||
                  result.slide.type === 'aiAssessedImageAnswers';
                const { icon } = toIcon(result.slide.type);
                const slideIndex = onlyFirstRelatedSlides.findIndex(
                  s => s.id === result.slide.id,
                );
                return (
                  <SummaryContainerItem
                    key={index}
                    id={`compact-slide-${result.slide.id}`}
                    style={{ paddingBottom: '40px' }}
                  >
                    <StudentResponseQuestion
                      style={{ display: 'flex', alignItems: 'flex-start' }}
                    >
                      <Label
                        style={{
                          cursor: 'pointer',
                          backgroundColor: theme.colors.greyDark,
                        }}
                        onClick={() => {
                          setSelectedSlide(result.slide);
                          setShowSlideModal(true);
                        }}
                      >
                        Open slide {slideIndex + 1}
                      </Label>
                      <IconWrapper
                        height="20px"
                        width="20px"
                        style={{ marginRight: 5, marginTop: 2 }}
                        color="#5B5B5A"
                        onClick={() => {
                          setSelectedSlide(result.slide);
                          setSelectedTab('activity');
                        }}
                      >
                        {icon}
                      </IconWrapper>
                      {result.title}
                    </StudentResponseQuestion>
                    <StudentResponseDivider />
                    <div>
                      {result.responses.map((r, i, all) => (
                        <div key={i}>
                          {r.response.strength && (
                            <>
                              <StudentResponseAnswer>
                                <span style={{ color: '#5B5B5A' }}>
                                  {t('greatPick', 'Great pick: ')}
                                </span>{' '}
                                {r.response.strength.description}
                              </StudentResponseAnswer>
                              <StudentResponseDivider />
                            </>
                          )}
                          {isDrawing && (
                            <div>
                              <LazyDrawingResponse
                                tenantId={tenantId || ''}
                                onClick={onImageClicked}
                                response={r.response}
                              />
                            </div>
                          )}
                          {!isDrawing && (
                            <StudentResponseAnswer>
                              <span style={{ color: '#5B5B5A' }}>
                                {all.length > 1
                                  ? t('answerWithNumber_new', 'Answer {number}', {
                                      number: i + 1,
                                    })
                                  : t('answer_', 'Answer')}
                                :
                              </span>{' '}
                              {r.response.response}
                              {result.slide.type === 'multipleChoice' &&
                                typeof r.isCorrect === 'boolean' && (
                                  <McAnswerBadge $correct={r.isCorrect}>
                                    {r.isCorrect
                                      ? t('Correct', 'Correct')
                                      : t('Incorrect', 'Incorrect')}
                                  </McAnswerBadge>
                                )}
                            </StudentResponseAnswer>
                          )}
                          {result.votingEnabled && (
                            <StudentResponseAnswer>
                              <span
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  width: '100%',
                                  gap: '5px',
                                  marginBottom: '10px',
                                  color: theme.colors.black,
                                }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                  <IconWrapper
                                    style={{ marginRight: 5 }}
                                    color={theme.colors.yellow}
                                  >
                                    <StarFilledSvg />
                                  </IconWrapper>
                                  <span style={{ color: '#5B5B5A' }}>Votes:</span>
                                </span>
                                {r.score}
                              </span>
                            </StudentResponseAnswer>
                          )}

                          {isAIFeedback && (
                            <>
                              <StudentResponseDivider />
                              {result.showScore && typeof result.aiScore === 'number' && (
                                <StudentResponseAnswer>
                                  <span style={{ fontWeight: 'bold', color: '#5B5B5A' }}>
                                    Score:
                                  </span>{' '}
                                  {result.aiScore}
                                </StudentResponseAnswer>
                              )}
                              <StudentResponseAnswer>
                                <span style={{ fontWeight: 'bold', color: '#5B5B5A' }}>
                                  Feedback:
                                </span>{' '}
                                {result.feedback}
                              </StudentResponseAnswer>
                            </>
                          )}
                          {i < result.responses.length - 1 && <StudentResponseDivider />}
                        </div>
                      ))}
                      {result.responses.length === 0 && (
                        <StudentResponseAnswer style={{ opacity: 0.7 }}>
                          No response submitted
                        </StudentResponseAnswer>
                      )}
                    </div>
                  </SummaryContainerItem>
                );
              })}
          </StudentInformationContainer>
        )}
      </StudentDataContainer>
    </StudentsTabPaneContainer>
  );
};

const StudentResponseQuestion = styled.span`
  font-size: 16px;
`;
const StudentResponseAnswer = styled.p`
  font-size: 14px;
  margin: 5px;
  white-space: pre-wrap;
`;
const StudentResponseDivider = styled(Divider)`
  margin: 5px 0;
`;
const McAnswerBadge = styled.span<{ $correct: boolean }>`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  background: ${({ $correct }) => ($correct ? '#e5f5ee' : '#fdecea')};
  color: ${({ $correct }) => ($correct ? '#0d7e64' : '#b3261e')};
  border: 1px solid ${({ $correct }) => ($correct ? '#a8ddcc' : '#f3b5b0')};
`;
const StudentSummaryView = ({
  participant,
  livePresentationId,
  activityResults,
  setSelectedTab,
  setSelectedSlide,
  reportIsApprovedForInsight,
  numberOfParticipants,
}: {
  participant: Participant;
  livePresentationId: string;
  activityResults?: ActivityResults;
  setSelectedTab: (tab: string) => void;
  setSelectedSlide: Dispatch<SetStateAction<Slide | undefined>>;
  reportIsApprovedForInsight?: boolean;
  numberOfParticipants?: number;
}) => {
  const metrics = useMetrics();
  const isLargerThan500 = useBreakPoint(500);
  const { data: studentSummary } = useReportInsights({
    reportType: 'studentSummary',
    livePresentationId,
    participantId: participant.id,
    reportIsApprovedForInsight,
  });
  const [enterpriseGetAllStudentInsights, setEnterpriseGetAllStudentInsights] = useState<
    boolean
  >(false);

  const handleSwitchGetAllStudentInsight = useCallback(
    (bool: boolean) => {
      metrics.logEvent('Report.Insight.AllStudents.Switch');
      setEnterpriseGetAllStudentInsights(bool);
    },
    [metrics],
  );
  const {
    generateInsight,
    error,
    generateInsightForAllStudents,
    isLoading,
    isLoadingAllStudent,
  } = useGenerateReportInsight();
  const handleGetStudentInsight = useCallback(() => {
    if (generateInsightForAllStudents && enterpriseGetAllStudentInsights) {
      metrics.logEvent('Report.Insight.AllStudents.Generate', {
        numberOfParticipants,
      });
      generateInsightForAllStudents();
      return;
    }
    metrics.logEvent('Report.Insight.Student.Generate');
    generateInsight({
      reportType: 'studentSummary',
      participantId: participant.id,
    });
  }, [
    enterpriseGetAllStudentInsights,
    generateInsight,
    generateInsightForAllStudents,
    metrics,
    numberOfParticipants,
    participant.id,
  ]);

  return (
    <StudentsSummaryContainer>
      <ParticipantsMatrixContainer style={{ width: '100%' }}>
        <InsightText>Participation</InsightText>
        <ReportParticipantMatrix
          activityResults={activityResults}
          participantsList={[participant]}
          reportTab={'students'}
          setSelectedTab={setSelectedTab}
          showTotalParticipationPercentage={false}
          setSelectedSlide={setSelectedSlide}
          onScrollIntoView={(slide: Slide) => {
            const element = document.getElementById(`compact-slide-${slide.id}`);
            if (!element) return;
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
              inline: 'center',
            });
          }}
        />
      </ParticipantsMatrixContainer>
      {reportIsApprovedForInsight && (
        <ReportInsight
          participantsList={[participant]}
          reportType="studentSummary"
          label="Student summary"
          livePresentationId={livePresentationId}
          participantId={participant.id}
          setSelectedTab={setSelectedTab}
        />
      )}

      {!studentSummary && reportIsApprovedForInsight && (
        <SummaryContainerItem style={{ padding: '10px 20px 20px 20px ' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <InsightText style={{ fontSize: 16, marginBottom: 2 }}>Summary</InsightText>
            {isLargerThan500 && (
              <span
                style={{ display: 'flex', width: 'fit-content', alignItems: 'center' }}
              >
                <Tooltip title="Request school access to unlock">
                  <p style={{ marginBottom: 0, marginRight: 5, whiteSpace: 'nowrap' }}>
                    Get insights for all students
                  </p>
                </Tooltip>
                <Switch
                  style={{ marginLeft: 10 }}
                  color={theme.colors.primary}
                  value={enterpriseGetAllStudentInsights}
                  onChange={(bool, e) => {
                    e.stopPropagation();
                    handleSwitchGetAllStudentInsight(bool);
                  }}
                />
              </span>
            )}
          </div>
          <InsightText style={{ marginBottom: 10 }}>
            Click the button below to generate insights about how{' '}
            {enterpriseGetAllStudentInsights
              ? 'this, and all other students'
              : 'this student'}{' '}
            performed in this class.
          </InsightText>
          <GetStudentInsightContainer>
            <CreateButton
              icon={<CuripodAIStarsSvg />}
              onClick={e => {
                debounceEventIfNotTrusted({
                  cb: handleGetStudentInsight,
                  isTrusted: e.isTrusted,
                });
              }}
              loading={isLoading}
              disabled={isLoadingAllStudent || !!error}
            >
              <T
                keyName={
                  enterpriseGetAllStudentInsights
                    ? 'Generate all student insights'
                    : 'Generate student insight'
                }
                defaultValue={
                  enterpriseGetAllStudentInsights
                    ? 'Generate all student insights'
                    : 'Generate student insight'
                }
              />
              <span
                style={{
                  fontSize: 12,
                  color: theme.colors.orange,
                  background: 'rgba(255,255,255,0.8)',
                  borderRadius: 7,
                  padding: '0 8px',
                  marginLeft: 10,
                }}
              >
                <T keyName="ai" defaultValue="AI" />
              </span>
            </CreateButton>
            {!!error && (
              <Alert
                message="We're sorry! 😔 There was an error generating feedback"
                type="error"
              />
            )}
          </GetStudentInsightContainer>
        </SummaryContainerItem>
      )}
    </StudentsSummaryContainer>
  );
};
const StudentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: start;
`;

const GetStudentInsightContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
`;
const StudentsSummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  max-width: 500px;

  gap: 10px;
  width: 100%;
  align-items: start;
`;

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
  justify-content: center;

  p {
    margin: 0;
    display: flex;
    align-items: center;
  }
`;

const StudentsTabPaneContainer = styled.div`
  display: grid;
  grid-template-rows: 150px auto;
  width: 100%;
  min-width: 200px;
  gap: 20px;
`;

const StudentDataContainer = styled.div`
  display: grid;
  grid-template-columns: 236px minmax(200px, 1fr);
  width: 100%;
  min-width: 200px;
  gap: 20px;
  padding: 0px 20px;
  @media (max-width: 800px) {
    grid-template-columns: minmax(200px, 500px);
  }
`;
const StudentInformationContainer = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
  align-items: start;
  padding-bottom: 100px;
`;

const SwitchContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const SwitchLabel = styled.span`
  font-weight: bold;
  padding-left: 10px;
`;

const StudentSelectContainer = styled.div`
  width: 100%;

  .ant-select-selector {
    border: 2px solid ${theme.colors.grey} !important;
    cursor: pointer !important;
  }
  .ant-select-selection-item {
    font-weight: bold;
  }
`;

const ActivityTabPane = ({
  game,
  setSelectedSlide,
  setSelectedParticipant,
  selectedSlide,
  tenantId,
  slides = [],
  slidesToRender = [],
  gameId,
  activityResults,
  setSelectedTab,
  activityType,
  selectedSlidesResultAllParticipants,
}: {
  game: Game;
  slides?: Slide[];
  slidesToRender: Slide[];
  gameId: string;
  setSelectedTab: (tab: string) => void;
  participantsList?: Participant[];
  setSelectedParticipant: Dispatch<Participant | undefined>;
  selectedParticipant?: Participant;
  selectedSlide?: Slide;
  setSelectedSlide: Dispatch<SetStateAction<Slide | undefined>>;
  activityType: SlideType;
  tenantId?: string;
  activityResults: ActivityResults;
  selectedSlidesResultAllParticipants?: ActivitySlideResult;
}) => {
  const metrics = useMetrics();
  const [
    shouldOnlyShowInactiveParticipants,
    setShouldOnlyShowInactiveParticipants,
  ] = useState(false);
  const { imagePreviewComponent, onImageClicked } = useImagePreview();
  const relatedSelectedSlides = useMemo(() => {
    if (!selectedSlide) return;
    return findRelatedSlidesWithoutIndex(selectedSlide.id, slides);
  }, [selectedSlide, slides]);
  const votingEnabled = useMemo(
    () => !!relatedSelectedSlides?.[1] && relatedSelectedSlides?.[1].type === 'voting',
    [relatedSelectedSlides],
  );
  if (!selectedSlide) return null;
  if (relatedSelectedSlides === undefined) return null;

  return (
    <ActivitiesContainer>
      {imagePreviewComponent}
      <Hotkeys
        keyName={'right'}
        onKeyDown={(_, event) => {
          event.preventDefault();
        }}
        onKeyUp={() => {
          if (!slidesToRender) return;
          if (!selectedSlide) return;

          const indexOfSelectedSlide = slidesToRender.findIndex(
            s => s.id === selectedSlide?.id,
          );
          if (indexOfSelectedSlide === -1) {
            setSelectedSlide(slidesToRender[0]);
            return;
          }
          // If the selected slide is the last one, select the first one
          if (indexOfSelectedSlide === slidesToRender.length - 1) {
            setSelectedSlide(slidesToRender[0]);
            return;
          }
          setSelectedSlide(slidesToRender[indexOfSelectedSlide + 1]);
        }}
      />
      <Hotkeys
        keyName={'left'}
        onKeyDown={(_, event) => {
          event.preventDefault();
        }}
        onKeyUp={() => {
          if (!slidesToRender) return;
          if (!selectedSlide) return;
          const indexOfSelectedSlide = slidesToRender.findIndex(
            s => s.id === selectedSlide?.id,
          );
          if (indexOfSelectedSlide === -1) {
            setSelectedSlide(slidesToRender[0]);
            return;
          }
          // If the selected slide is the first one, select the last one
          if (indexOfSelectedSlide === 0) {
            setSelectedSlide(slidesToRender[slidesToRender.length - 1]);
            return;
          }
          setSelectedSlide(slidesToRender[indexOfSelectedSlide - 1]);
        }}
      />
      <SlideHorizontalListView
        slidesToRender={slidesToRender}
        gameId={gameId}
        slides={slides || []}
        tenantId={tenantId}
        setSelectedSlide={setSelectedSlide}
        selectedSlide={selectedSlide}
        reportTab={'activity'}
        shouldShowSlideInstructions={true}
        lessonAttachments={game?.attachments}
      />
      <ActivityContainerContent>
        {selectedSlidesResultAllParticipants && (
          <ActivitySummaryContainer>
            <p
              style={{
                fontSize: '30px',
                fontWeight: 'bold',
                textAlign: 'center',
                marginBottom: 0,
              }}
            >
              {'Total respondents'}
            </p>
            <Progress
              type="circle"
              width={60}
              percent={
                (selectedSlidesResultAllParticipants.activeParticipants /
                  activityResults.totalParticipants) *
                100
              }
              format={() =>
                `${selectedSlidesResultAllParticipants.activeParticipants} / ${activityResults.totalParticipants}`
              }
              strokeColor={getParticipantPercentageColor(
                selectedSlidesResultAllParticipants.activeParticipants /
                  activityResults.totalParticipants,
              )}
            />
          </ActivitySummaryContainer>
        )}

        {!selectedSlidesResultAllParticipants && (
          <EmptyStateContainer>
            <EmojiWrapper>
              <AppreciateSvg />
            </EmojiWrapper>
            <p style={{ color: theme.colors.black }}>
              This is a slide without student responses{' '}
            </p>
            <span style={{ display: 'flex' }}>
              Use the arrow keys{' '}
              <IconWrapper width="25px" height="25px">
                <ArrowLeftSvg />
              </IconWrapper>{' '}
              and{' '}
              <IconWrapper width="25px" height="25px">
                <ArrowRightSvg />{' '}
              </IconWrapper>
              to go the next slide{' '}
            </span>
          </EmptyStateContainer>
        )}
        {selectedSlidesResultAllParticipants && (
          <ActivityResponsesContainer>
            {selectedSlide && (
              <SwitchContainer
                style={{
                  width: 'fit-content',
                  alignSelf: 'end',
                  marginTop: 10,
                  marginBottom: 3,
                }}
              >
                <SwitchLabel>Show only students that did not participate</SwitchLabel>
                <Switch
                  style={{ marginLeft: 10 }}
                  color={theme.colors.orange}
                  value={shouldOnlyShowInactiveParticipants}
                  onChange={(bool, e) => {
                    metrics.logEvent(
                      'Report.Insight.Activity.Switch.StudentThatDidNotParticipate',
                    );
                    e.stopPropagation();
                    setShouldOnlyShowInactiveParticipants(
                      !shouldOnlyShowInactiveParticipants,
                    );
                  }}
                />
              </SwitchContainer>
            )}
            <ActivityResponseList
              selectedSlidesResultAllParticipants={selectedSlidesResultAllParticipants}
              setSelectedTab={setSelectedTab}
              activityType={activityType}
              tenantId={tenantId}
              votingEnabled={votingEnabled}
              setSelectedParticipant={setSelectedParticipant}
              participantsById={activityResults.participants}
              pollOptions={selectedSlide.pollOptions || []}
              shouldOnlyShowInactiveParticipants={shouldOnlyShowInactiveParticipants}
              onImageClicked={onImageClicked}
              // Pass the activity slide (head of the chain) rather than the
              // user's selected slide — `slidesToRender` filters in the
              // responsegrid slide for played activities, and that slide has
              // no `prompts` array. `hasScorePromptOutput` needs the activity
              // slide to detect the `score` output and render the Score column.
              slide={relatedSelectedSlides?.[0] || selectedSlide}
            />
          </ActivityResponsesContainer>
        )}
      </ActivityContainerContent>
    </ActivitiesContainer>
  );
};

type ActivityTableData = {
  key: string;
  name: string;
  response: Response;
  aiFeedback?: AiAssessedScore;
  participant: Participant;
  activityType: string;
  votes?: number;
  didNotParticipate?: boolean;
};
const ActivityResponseList = ({
  selectedSlidesResultAllParticipants,
  participantsById,
  pollOptions,
  activityType,
  setSelectedParticipant,
  setSelectedTab,
  tenantId,
  shouldOnlyShowInactiveParticipants,
  onImageClicked,
  votingEnabled,
  slide,
}: {
  selectedSlidesResultAllParticipants: ActivitySlideResult;
  participantsById: ById<Participant>;
  activityType: SlideType;
  tenantId?: string;
  pollOptions: PollOptions[];
  slide?: Slide;
  setSelectedTab: (tab: string) => void;
  setSelectedParticipant: Dispatch<Participant | undefined>;
  shouldOnlyShowInactiveParticipants: boolean;
  onImageClicked: Dispatch<SetStateAction<string | undefined>>;
  votingEnabled?: boolean;
}) => {
  const { t } = useTranslate();
  const handleNameClick = useCallback(
    (participant: Participant) => {
      setSelectedParticipant(participant);
      setSelectedTab('students');
    },
    [setSelectedParticipant, setSelectedTab],
  );
  const tableData: ActivityTableData[] = useMemo(() => {
    if (!selectedSlidesResultAllParticipants) return [];

    //sort activity results by score participant name alphabetically
    selectedSlidesResultAllParticipants.scores.sort((a, b) => {
      const aParticipant = participantsById?.[a.response.uid];
      const bParticipant = participantsById?.[b.response.uid];
      if (!aParticipant || !bParticipant) return 0;
      if (
        (aParticipant.name || aParticipant.nickName) <
        (bParticipant.name || bParticipant.nickName)
      ) {
        return -1;
      }
      if (
        (aParticipant.name || aParticipant.nickName) <
        (bParticipant.name || bParticipant.nickName)
      ) {
        return 1;
      }
      return 0;
    });

    return Object.values(selectedSlidesResultAllParticipants.scores)
      .map((score: Score, i: number) => {
        const participantUid = score.response.uid;
        const participant = participantsById?.[participantUid];
        if (shouldOnlyShowInactiveParticipants) {
          if (score.response.id === 'nonResponse') {
            return {
              key: `${score.response.id}-${i}`,
              name: participant.name
                ? `${participant.name} (${participant.nickName})`
                : participant.nickName,
              response: score.response,
              aiFeedback: score.feedback,
              participant: participant,
              votes: 0,
              didNotParticipate: score.response.id === 'nonResponse',
              activityType,
            };
          } else return;
        }
        return {
          key: `${score.response.id}-${i}`,
          name: participant.name
            ? `${participant.name} (${participant.nickName})`
            : participant.nickName,
          response: score.response,
          aiFeedback: score.feedback,
          votes: score.score,
          participant: participant,
          didNotParticipate: score.response.id === 'nonResponse',
          activityType,
        };
      })
      .filter(notUndefined);
  }, [
    selectedSlidesResultAllParticipants,
    activityType,
    participantsById,
    shouldOnlyShowInactiveParticipants,
  ]);

  const tableColumns = useMemo(() => {
    const columns = [
      {
        title: 'Student',
        dataIndex: 'name',
        sorter: (a: ActivityTableData, b: ActivityTableData) => {
          if (a.name < b.name) {
            return -1;
          }
          if (a.name > b.name) {
            return 1;
          }
          return 0;
        },
        render(_: string, record: ActivityTableData) {
          return (
            <NameContainer
              style={{
                color: record.didNotParticipate
                  ? theme.colors.iconGrey
                  : theme.colors.black,
              }}
              onClick={() => handleNameClick(record.participant)}
            >
              {record.name}
            </NameContainer>
          );
        },
      },
      {
        title: 'Response',
        dataIndex: ['response', 'activityType'],
        render: (_: string, record: ActivityTableData) => {
          if (
            (record.activityType === 'canvas' ||
              record.activityType === 'aiAssessedImageAnswers') &&
            tenantId
          ) {
            if (record.didNotParticipate) {
              return (
                <span style={{ whiteSpace: 'pre-wrap', color: theme.colors.iconGrey }}>
                  {record.response.response}
                </span>
              );
            }
            return (
              <LazyDrawingResponse
                tenantId={tenantId}
                style={{
                  height: 'calc(100% - 24px)',
                  width: '100%',
                  borderRadius: 10,
                  backgroundColor: 'white',
                }}
                onClick={onImageClicked}
                boxShadow={'0px 2px 20px rgba(0,0,0,0.15)'}
                response={record.response}
              />
            );
          }
          if (record.activityType === 'poll') {
            const pollOption = pollOptions.find(p => p.id === record.response.response);
            if (pollOption) {
              return (
                <span
                  style={{
                    whiteSpace: 'pre-wrap',
                    color: record.didNotParticipate
                      ? theme.colors.iconGrey
                      : pollOption.color,
                  }}
                >
                  {parseSlateStringToText(pollOption.displayName)}
                </span>
              );
            }
          }
          if (record.activityType === 'multipleChoice') {
            const mcOption = pollOptions.find(p => p.id === record.response.response);
            if (mcOption) {
              return (
                <span style={{ whiteSpace: 'pre-wrap' }}>
                  {parseSlateStringToText(mcOption.displayName)}
                  {!record.didNotParticipate && (
                    <McAnswerBadge $correct={!!mcOption.isCorrect}>
                      {mcOption.isCorrect
                        ? t('Correct', 'Correct')
                        : t('Incorrect', 'Incorrect')}
                    </McAnswerBadge>
                  )}
                </span>
              );
            }
          }
          return (
            <span
              style={{
                whiteSpace: 'pre-wrap',
                color: record.didNotParticipate
                  ? theme.colors.iconGrey
                  : theme.colors.black,
              }}
            >
              {record.response.response}
            </span>
          );
        },
      },
    ];
    if (
      activityType === 'aiAssessedAnswers' ||
      activityType === 'aiAssessedImageAnswers'
    ) {
      const showScore = !!slide && hasScorePromptOutput(slide);
      return [
        ...columns,
        ...(showScore
          ? [
              {
                title: 'Score',
                dataIndex: 'score',
                render: (_: string, record: ActivityTableData) => {
                  if (typeof record.aiFeedback?.aiScore !== 'number') return null;
                  return (
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {record.aiFeedback.aiScore}
                    </span>
                  );
                },
              },
            ]
          : []),
        {
          title: 'AI Feedback',
          dataIndex: 'aiFeedback',
          render: (_: string, record: ActivityTableData) => {
            if (!record.aiFeedback) return null;
            return (
              <span
                style={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                {record.aiFeedback.feedback}
              </span>
            );
          },
        },
        ...(activityType === 'aiAssessedAnswers'
          ? [
              {
                title: 'Great Pick',
                dataIndex: 'greatPick',
                render: (_: string, record: ActivityTableData) => {
                  return <span>{record.response.strength?.description}</span>;
                },
              },
            ]
          : []),
      ];
    }
    if (votingEnabled) {
      return [
        ...columns,
        {
          title: 'Votes',
          dataIndex: 'votes',
          render: (_: string, record: ActivityTableData) => {
            return (
              <span
                style={{
                  whiteSpace: 'pre-wrap',
                }}
              >
                {record.votes}
              </span>
            );
          },
        },
      ];
    }
    return columns;
  }, [
    activityType,
    handleNameClick,
    onImageClicked,
    pollOptions,
    tenantId,
    votingEnabled,
    slide,
    t,
  ]);
  return (
    <ActivityResponseListContainer>
      {tableData.length === 0 && (
        <EmptyStateContainer>
          <EmojiWrapper>
            <AppreciateSvg />
          </EmojiWrapper>
          <p style={{ color: theme.colors.black }}>No responses to show</p>
        </EmptyStateContainer>
      )}
      {tableData.length > 0 && (
        <Table dataSource={tableData} columns={tableColumns} pagination={false} />
      )}
    </ActivityResponseListContainer>
  );
};
const NameContainer = styled.div`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;
const ActivityResponsesContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const ActivityResponseListContainer = styled.div``;
const ActivitiesContainer = styled.div`
  display: grid;
  grid-template-rows: 180px 1fr;
  align-items: flex-start;
  width: 100%;
`;
const ActivitySummaryContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;

  gap: 10px;
  justify-content: flex-start;
  align-items: center;
  align-content: center;
  justify-items: center;
  .ant-progress-circle.ant-progress-status-success,
  .ant-progress-text {
    color: ${theme.colors.black} !important;
  }
`;
const ActivityContainerContent = styled.div`
  padding: 0px 20px;
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  height: 100%;
`;
const SlideActionButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  bottom: 50px;
  position: absolute;
  right: 50px;
  color: white;
  z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};

  background-color: rgba(40, 47, 51, 0.85);
  padding: 20px;
  border-radius: 10px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
`;

const SlideActionButton = styled.div`
  color: white;
  display: flex;
  font-weight: bold;
  cursor: pointer;
  gap: 10px;
  font-size: 40px;

  &:hover {
    opacity: 0.8;
  }
`;

const FixedSlidePositionContainer = styled.div<{ $isFixed?: boolean }>`
  ${({ $isFixed }) =>
    $isFixed &&
    css`
      position: fixed;
      padding-top: 20px;
      top: 0px;
      padding-left: 0px;
      left: 240px;
      width: calc(100vw - 240px);
      background-color: ${theme.colors.background};
      z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};
      @media (max-width: 1100px) {
        left: 0px;
        width: 100vw;
        top: 60px;
      }
    `}
`;

const FixedStudentSelectorPositionContainer = styled.div`
  position: fixed;
  padding-top: 20px;
  top: 50px;
  padding-left: 20px;
  left: 240px;
  background-color: ${theme.colors.background};
  z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};
  @media (max-width: 1100px) {
    left: 0px;
  }
`;

const SlideHorizontalListView = ({
  slides = [],
  gameId,
  selectedSlide,
  tenantId,
  setSelectedSlide,
  slidesToRender,
  shouldShowSlideInstructions,
  reportTab,
  lessonAttachments,
}: {
  slidesToRender: Slide[];
  slides?: Slide[];
  gameId: string;
  tenantId?: string;
  reportTab: 'activity' | 'students';
  selectedSlide: Slide | undefined;
  setSelectedSlide: Dispatch<SetStateAction<Slide | undefined>>;
  shouldShowSlideInstructions: boolean;
  lessonAttachments?: LessonAttachment[];
}) => {
  const { t } = useTranslate();
  const { scrollPosition } = useScrollPosition();
  const [showSlideModal, setShowSlideModal] = useState(false);
  const [showResultsView, setShowResultsView] = useState<boolean>(true);
  const handleRightArrowClicked = () => {
    if (!slidesToRender) return;
    if (!selectedSlide) return;

    const indexOfSelectedSlide = slidesToRender.findIndex(
      s => s.id === selectedSlide?.id,
    );
    if (indexOfSelectedSlide === -1) {
      setSelectedSlide(slidesToRender[0]);
      return;
    }
    // If the selected slide is the last one, select the first one
    if (indexOfSelectedSlide === slidesToRender.length - 1) {
      setSelectedSlide(slidesToRender[0]);
      return;
    }
    setSelectedSlide(slidesToRender[indexOfSelectedSlide + 1]);
  };
  const handleLeftArrowClicked = () => {
    if (!slidesToRender || slidesToRender.length === 0) return;
    if (!selectedSlide) return;
    const indexOfSelectedSlide = slidesToRender.findIndex(
      s => s.id === selectedSlide?.id,
    );
    if (indexOfSelectedSlide === -1) {
      setSelectedSlide(slidesToRender[0]);
      return;
    }
    // If the selected slide is the first one, select the last one
    if (indexOfSelectedSlide === 0) {
      setSelectedSlide(slidesToRender[slidesToRender.length - 1]);
      return;
    }
    setSelectedSlide(slidesToRender[indexOfSelectedSlide - 1]);
  };

  const slideData = useMemo(
    () => ({
      slides,
      slidesToRender,
      shouldShowSlideInstructions,
      gameId,
      tenantId,
      selectedSlide,
      reportTab,
      setSelectedSlide,
      lessonAttachments,
    }),
    [
      slides,
      slidesToRender,
      shouldShowSlideInstructions,
      gameId,
      tenantId,
      selectedSlide,
      reportTab,
      setSelectedSlide,
      lessonAttachments,
    ],
  );

  const scrollToListRef = useRef<List>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const previouslySelectedId = usePrevious(selectedSlide?.id);
  useEffect(() => {
    if (selectedSlide && previouslySelectedId !== selectedSlide?.id) {
      const index = slidesToRender.findIndex(slide => slide.id === selectedSlide.id);
      scrollToListRef?.current?.scrollToItem(index, 'smart');
      if (!scrollToListRef.current) {
        // Hack to handle the split second when the scrollToListRef is not set yet, but we have the data we need
        setTimeout(() => {
          scrollToListRef?.current?.scrollToItem(index, 'smart');
        }, 500);
      }
    }
  }, [selectedSlide, previouslySelectedId, slidesToRender]);
  const relatedSelectedSlides = useMemo(() => {
    if (!selectedSlide) return;
    return findRelatedSlidesWithoutIndex(selectedSlide.id, slides);
  }, [selectedSlide, slides]);

  const onScroll = (
    props: ListOnScrollProps,
    itemSize: number,
    itemCount: number,
    listWidth: number,
  ) => {
    if (props.scrollOffset < 50) {
      setShowLeftArrow(false);
    } else {
      setShowLeftArrow(true);
    }

    if (props.scrollOffset > itemSize * itemCount - listWidth - 50) {
      setShowRightArrow(false);
    } else {
      setShowRightArrow(true);
    }
  };

  return (
    <div style={{ height: 'fit-content', width: '100%' }}>
      {scrollPosition > 180 && <div></div>}

      <FixedSlidePositionContainer $isFixed={scrollPosition > 180}>
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
          {selectedSlide && showLeftArrow && (
            <GeneratorsLeftArrowContainer>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  height: '100%',
                }}
              >
                <StyledArrowButton
                  styles={{
                    background: theme.colors.white,
                    border: 'none',
                    height: 35,
                    width: 35,
                    borderRadius: '50%',
                    color: theme.colors.black,
                    transform: 'translateX(20px)',
                  }}
                  type="ghost"
                  icon={<ArrowLeftSvg />}
                  notext={true}
                  onClick={() => {
                    handleLeftArrowClicked();
                  }}
                />
              </div>
            </GeneratorsLeftArrowContainer>
          )}
          <SlideHorizontalListViewContainer>
            <AutoSizer>
              {({ width }: { height: number; width: number }) => {
                return (
                  <List
                    ref={scrollToListRef}
                    style={{ scrollbarWidth: 'none' }}
                    onScroll={props => onScroll(props, 245, slidesToRender.length, width)}
                    height={140}
                    width={width}
                    className="no-scrollbars"
                    itemCount={slidesToRender.length}
                    itemSize={245}
                    layout="horizontal"
                    itemData={slideData}
                  >
                    {Column}
                  </List>
                );
              }}
            </AutoSizer>
          </SlideHorizontalListViewContainer>
          {selectedSlide && showRightArrow && (
            <GeneratorsRightArrowContainer>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  height: '100%',
                }}
              >
                <StyledArrowButton
                  styles={{
                    background: theme.colors.white,
                    border: 'none',
                    height: 35,
                    width: 35,
                    right: 5,
                    borderRadius: '50%',
                    color: theme.colors.black,
                    transform: 'translateX(-20px)',
                  }}
                  type="ghost"
                  icon={<ArrowRightSvg />}
                  notext={true}
                  onClick={() => {
                    handleRightArrowClicked();
                  }}
                />
              </div>
            </GeneratorsRightArrowContainer>
          )}
        </div>
      </FixedSlidePositionContainer>
      {showSlideModal && relatedSelectedSlides && selectedSlide && (
        <FullScreenSlidePreview onClose={() => setShowSlideModal(false)}>
          <LiveActionsDisabledProvider>
            <CSSSlideWrapperViewer
              aspect={{ height: 1080, width: 1920 }}
              slide={selectedSlide}
              backgroundMedia={relatedSelectedSlides[0].backgroundMedia}
              slides={slides}
              resultView={showResultsView}
              showStaticPoll={!showResultsView}
              gameId={gameId}
              tenantId={tenantId}
              hideLogo={false}
              isDiscoverTemplate={false}
              isOwner={true}
              isAuthenticated={true}
              isUsingSockets={false}
              uniqueId={`slide-result-view-${selectedSlide.id}`}
              lessonAttachments={lessonAttachments}
            >
              {selectedSlide.type !== 'slide' && selectedSlide.wasPlayed && (
                <SlideActionButtonContainer>
                  {showResultsView && (
                    <SlideActionButton
                      onClick={() => {
                        setShowResultsView(false);
                      }}
                    >
                      <IconWrapper width="50px" height="50px">
                        <MediaIconSvg />
                      </IconWrapper>
                      {t('showSlide', 'Show Slide')}
                    </SlideActionButton>
                  )}
                  {!showResultsView && (
                    <SlideActionButton
                      onClick={() => {
                        setShowResultsView(true);
                      }}
                    >
                      <IconWrapper width="50px" height="50px">
                        <FlashSvg />
                      </IconWrapper>
                      {t('showResults', 'Show results')}
                    </SlideActionButton>
                  )}
                </SlideActionButtonContainer>
              )}
            </CSSSlideWrapperViewer>
          </LiveActionsDisabledProvider>
        </FullScreenSlidePreview>
      )}
    </div>
  );
};

const Column = ({
  data,
  index,
  style,
}: {
  data: {
    slides: Slide[];
    slidesToRender: Slide[];
    shouldShowSlideInstructions: boolean;
    gameId: string;
    tenantId?: string;
    selectedSlide: Slide | undefined;
    reportTab: 'activity' | 'students';
    setSelectedSlide: Dispatch<SetStateAction<Slide | undefined>>;
    lessonAttachments?: LessonAttachment[];
  };
  index: number;
  style: CSSProperties;
}) => {
  const {
    slides,
    slidesToRender,
    shouldShowSlideInstructions,
    gameId,
    tenantId,
    selectedSlide,
    reportTab,
    setSelectedSlide,
    lessonAttachments,
  } = data;
  const { t } = useTranslate();
  const slide = slidesToRender[index];
  const relatedSlides = findRelatedSlidesWithoutIndex(slide.id, slides);
  const [showSlideModal, setShowSlideModal] = useState(false);
  const [showResultsView, setShowResultsView] = useState(false);
  const relatedSelectedSlides = useMemo(() => {
    if (!selectedSlide) return;
    return findRelatedSlidesWithoutIndex(selectedSlide.id, slides);
  }, [selectedSlide, slides]);

  const isSelected =
    reportTab === 'activity' && !!selectedSlide && slide.id === selectedSlide.id;

  return (
    <div style={{ ...style }}>
      {showSlideModal && relatedSelectedSlides && selectedSlide && (
        <FullScreenSlidePreview onClose={() => setShowSlideModal(false)}>
          <LiveActionsDisabledProvider>
            <CSSSlideWrapperViewer
              aspect={{ height: 1080, width: 1920 }}
              slide={selectedSlide}
              backgroundMedia={relatedSelectedSlides[0].backgroundMedia}
              slides={slides}
              resultView={showResultsView}
              showStaticPoll={!showResultsView}
              gameId={gameId}
              tenantId={tenantId}
              hideLogo={false}
              isDiscoverTemplate={false}
              isOwner={true}
              isAuthenticated={true}
              isUsingSockets={false}
              uniqueId={`slide-result-view-${selectedSlide.id}`}
              lessonAttachments={lessonAttachments}
            >
              {selectedSlide.type !== 'slide' && selectedSlide.wasPlayed && (
                <SlideActionButtonContainer>
                  {showResultsView && (
                    <SlideActionButton
                      onClick={() => {
                        setShowResultsView(false);
                      }}
                    >
                      <IconWrapper width="50px" height="50px">
                        <MediaIconSvg />
                      </IconWrapper>
                      {t('showSlide', 'Show Slide')}
                    </SlideActionButton>
                  )}
                  {!showResultsView && (
                    <SlideActionButton
                      onClick={() => {
                        setShowResultsView(true);
                      }}
                    >
                      <IconWrapper width="50px" height="50px">
                        <FlashSvg />
                      </IconWrapper>
                      {t('showResults', 'Show results')}
                    </SlideActionButton>
                  )}
                </SlideActionButtonContainer>
              )}
            </CSSSlideWrapperViewer>
          </LiveActionsDisabledProvider>
        </FullScreenSlidePreview>
      )}
      <HorizontalSlideContainer>
        <LiveActionsDisabledProvider key={`slide${index}`}>
          <CSSSlideWrapperViewer
            aspect={{ height: 1080, width: 1920 }}
            slide={slide}
            backgroundMedia={relatedSlides[0].backgroundMedia}
            slides={slides}
            resultView={!shouldShowSlideInstructions && !!slide.wasPlayed}
            showStaticPoll={shouldShowSlideInstructions || !slide.wasPlayed}
            gameId={gameId}
            tenantId={tenantId}
            hideLogo={false}
            isDiscoverTemplate={false}
            isOwner={true}
            isAuthenticated={true}
            preventInteraction={true}
            isUsingSockets={false}
            uniqueId={`slide-list-${slide.id}`}
            lessonAttachments={lessonAttachments}
            onClickWithPreventInteraction={() => {
              if (reportTab === 'activity') {
                setSelectedSlide(slide);
              }
              if (reportTab === 'students') {
                setSelectedSlide(slide);
                setShowSlideModal(true);
              }
            }}
          >
            <>
              {isSelected && <SelectedSlideOverlay />}
              {slide.type !== 'slide' && (
                <ActivityOverlayItem>
                  <IconWrapper
                    color="white"
                    height={'150px'}
                    width={'150px'}
                    onClick={() => setShowSlideModal(true)}
                  >
                    {slideToFirstRelevanSlideIcon(slide, slides)}
                  </IconWrapper>
                </ActivityOverlayItem>
              )}
              {reportTab === 'activity' && (
                <ExpandActivityOverlay>
                  <ExpandActivityOverlayContainer
                    $isSelected={!!selectedSlide && slide.id === selectedSlide.id}
                    onClick={() => {
                      setSelectedSlide(slide);
                    }}
                  >
                    <ExpandActivityOverlayItem
                      onClick={() => {
                        setSelectedSlide(slide);
                        setShowSlideModal(true);
                      }}
                    >
                      <IconWrapper color="white" height={'150px'} width={'150px'}>
                        <FullScreenSvg />
                      </IconWrapper>
                    </ExpandActivityOverlayItem>
                  </ExpandActivityOverlayContainer>
                </ExpandActivityOverlay>
              )}
            </>
          </CSSSlideWrapperViewer>
        </LiveActionsDisabledProvider>
        <Label>{index + 1}</Label>
      </HorizontalSlideContainer>
    </div>
  );
};

const SelectedSlideOverlay = styled.div`
  position: absolute;
  z-index: 1;
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  background: transparent;
  border-radius: 10px;
  border: 20px solid ${theme.colors.blue};
`;

const ExpandActivityOverlay = styled.div`
  position: absolute;
  bottom: 5px;
  left: 5px;
  width: 100%;
  height: 100%;
  z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};
`;
const ExpandActivityOverlayContainer = styled.div<{ $isSelected: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  opacity: ${props => (props.$isSelected ? 1 : 0)};
  transition: opacity 0.5s ease-in-out;
  &:hover {
    opacity: 1;
  }
`;
const ExpandActivityOverlayItem = styled.div`
  position: absolute;
  bottom: -10px;
  left: -10px;
  border-radius: 0 50px 0 0;
  cursor: pointer;
  background-color: rgba(122, 122, 122, 0.76);
  padding: 100px;
`;
const Label = styled.div`
  position: absolute;
  bottom: 10px;
  right: 12px;
  background: #80808099;
  border-radius: 10px;
  color: ${theme.colors.white};
  font-weight: 600;
  font-size: 12px;
  padding: 2px 6px;
  height: 20px;
  display: flex;
  align-items: center;
  box-shadow: 0px 4px 4px #00000025;
`;

const StyledArrowButton = styled(Button)`
  padding: 0px;
  svg {
    height: 28px;
    width: 28px;
  }
`;

const GeneratorsRightArrowContainer = styled.div`
  position: absolute;
  height: 140px;
  width: 40px;
  left: calc(100vw - 280px);
  top: 0px;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
  @media (max-width: 1100px) {
    left: calc(100vw - 40px);
  }
`;
const GeneratorsLeftArrowContainer = styled.div`
  position: absolute;
  height: 140px;
  width: 40px;
  left: 0px;
  top: 0px;
  background: linear-gradient(
    to left,
    rgba(255, 255, 255, 0),
    ${theme.colors.background}
  );
  z-index: 1;
`;

const ActivityOverlayItem = styled.div`
  background-color: rgba(122, 122, 122, 0.76);
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 4px 0 4px 0;
  padding: 40px;
  border-radius: 0 0 20% 0;
  z-index: ${zIndexes('ACTIVITY_OVERLAY_ITEM')};
`;

const TextArea = styled.textarea`
  border-radius: 5px;
  resize: none;
  background-color: ${theme.colors.greyLight};
  border: none;
  max-height: 200px;
  min-height: 100px;
  font-weight: bold;
  width: 100%;
  &:focus {
    outline: none;
  }
`;

const SlideHorizontalListViewContainer = styled.div`
  width: 100%;
  height: 140px;
  padding: 0px;

  overflow-y: hidden;
  display: flex;
  gap: 10px;

  // Hide the horisontal scrollbars
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Hide scrollbar for Chrome, Safari and Opera */
  }
  .no-scrollbars {
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;

    &::-webkit-scrollbar {
      width: 1px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: transparent;
    }
  }

  .no-scrollbars::-webkit-scrollbar {
    display: none; /* Safari and Chrome */
  }

  @media (max-width: 800px) {
    height: 150px;
  }
`;
const HorizontalSlideContainer = styled.div`
  height: 133px;
  width: 236px;
  max-width: 236px;
  min-width: 236px;
  border-radius: 5px;
  overflow: hidden;
  margin-top: 4px;
  margin-left: 5px;
  box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
`;

const ResultTitle = styled.h1`
  display: flex;
  gap: 20px;
  align-items: center;
  font-size: 24px;
  margin: 0;
  height: 40px;
  padding: 0 20px;
  @media (max-width: 830px) {
    font-size: 20px;
  }
`;
const ResultsTab = styled(Tabs)`
  padding: 0px 20px;
  .ant-tabs-nav {
    margin: 0;
  }
  &&-active &-btn {
    text-shadow: 0 0 0.25px currentcolor;
  }
  .ant-tabs-nav-list {
    width: 0;
  }

  .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::after,
  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn,
  .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::before {
    box-shadow: none !important;

    font-weight: bold !important;

    color: ${theme.colors.orange} !important;
  }
  .ant-tabs-content {
    height: 100% !important;
  }
  .ant-tabs-tab {
    font-weight: bold !important;
  }
  &:hover,
  &:active,
  &:focus {
    .ant-tabs-tab {
      color: ${theme.colors.black};
    }
  }
  .ant-tabs-ink-bar {
    background: ${theme.colors.orange};
  }
`;
const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  ol {
    padding: 0;
  }
`;

const ShareButton = styled(Button)`
  color: ${theme.colors.white};
  background-color: ${theme.colors.blue};
  width: 150px;
  border: none;
  &:hover,
  &:active,
  &:focus {
    color: ${theme.colors.white};
    background-color: ${theme.colors.blue};
  }
`;
const ReportContainer = styled.div`
  width: 100%;
  height: calc(100% - 170px);
`;

const ReportContainerHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  justify-content: space-between;
  @media (max-width: 830px) {
    grid-template-columns: 1fr;
  }
`;
const ReportContainerHeaderContent = styled.div`
  padding: 0;
  width: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: ${theme.colors.beigeDark};
  clip-path: polygon(
    0% 0%,
    100% 0%,
    82.23% 91.626%,
    82.23% 91.626%,
    81.907% 93.156%,
    81.56% 94.544%,
    81.192% 95.786%,
    80.803% 96.877%,
    80.397% 97.812%,
    79.976% 98.588%,
    79.542% 99.199%,
    79.097% 99.641%,
    78.643% 99.909%,
    78.183% 100%,
    0% 100%,
    0% 0%
  );

  @media (max-width: 830px) {
    clip-path: unset;
  }
`;
const ReportContainerContent = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 20px 0px;

  @media (max-width: 1000px) {
    margin-bottom: 50px;
  }
`;

const ShareContainer = styled.div`
  display: flex;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  justify-content: left;
  gap: 5px;
`;
const ResultPageContentContainer = styled.div`
  padding-left: 240px;
  @media (max-width: 1100px) {
    padding-left: 0px;
    padding-bottom: 60px;
    padding-top: 60px;
  }
`;
export default ReportPage;
